import type { Db, Deal, Product, Transaction, User, UserRole } from './types'

export function isAgencyLikeRole(role?: UserRole | null): role is '代理店' | '総代理店' {
  return role === '代理店' || role === '総代理店'
}

export function isActiveUser(user?: User | null): user is User {
  return !!user && !user.deletedAt
}

export function getCurrentUser(db: Db): User {
  const me = db.sessionUserId ? db.users.find((u) => u.id === db.sessionUserId) : undefined
  if (!isActiveUser(me)) throw new Error('ログインが必要です')
  return me
}

export function findUser(db: Db, userId?: string): User | undefined {
  if (!userId) return undefined
  return db.users.find((u) => u.id === userId)
}

export function findProduct(db: Db, productId?: string): Product | undefined {
  if (!productId) return undefined
  return db.products.find((p) => p.id === productId)
}

export function findDeal(db: Db, dealId?: string): Deal | undefined {
  if (!dealId) return undefined
  return db.deals.find((d) => d.id === dealId)
}

export function findTransaction(db: Db, txId?: string): Transaction | undefined {
  if (!txId) return undefined
  return db.transactions.find((t) => t.id === txId)
}

export function superAgencies(db: Db): User[] {
  return db.users.filter((u) => isActiveUser(u) && u.role === '総代理店' && !!u.agency)
}

export function agencies(db: Db): User[] {
  return db.users.filter((u) => isActiveUser(u) && u.role === '代理店' && !!u.agency)
}

export function connectors(db: Db): User[] {
  return db.users.filter((u) => isActiveUser(u) && u.role === 'コネクター' && !!u.connector)
}

export function getSuperAgencyForAgency(db: Db, agencyUserId: string): User | undefined {
  const agency = findUser(db, agencyUserId)
  if (!isActiveUser(agency) || agency.role !== '代理店') return undefined
  const superAgency = findUser(db, agency.agency?.superAgencyId)
  return isActiveUser(superAgency) ? superAgency : undefined
}

export function agenciesForSuperAgency(db: Db, superAgencyUserId: string): User[] {
  return db.users.filter(
    (u) => isActiveUser(u) && u.role === '代理店' && u.agency?.superAgencyId === superAgencyUserId,
  )
}

export function getAgencyForConnector(db: Db, connectorUserId: string): User | undefined {
  const u = db.users.find((x) => x.id === connectorUserId)
  if (!isActiveUser(u) || u.role !== 'コネクター' || !u.connector) return undefined
  const connector = u.connector
  return db.users.find((a) => a.id === connector.agencyId && isActiveUser(a))
}

export function getConnectorsForAgency(db: Db, agencyUserId: string): User[] {
  return db.users.filter(
    (u) => isActiveUser(u) && u.role === 'コネクター' && u.connector?.agencyId === agencyUserId,
  )
}

// --- Backward-compatible selector aliases ---
export function connectorsForAgency(db: Db, agencyUserId: string): User[] {
  return getConnectorsForAgency(db, agencyUserId)
}

export function connectorsVisibleToPartner(db: Db, partnerUserId: string): User[] {
  const partner = findUser(db, partnerUserId)
  if (!isActiveUser(partner) || !isAgencyLikeRole(partner.role)) return []

  if (partner.role === '代理店') {
    return connectorsForAgency(db, partner.id)
  }

  const direct = connectorsForAgency(db, partner.id)
  const subordinate = agenciesForSuperAgency(db, partner.id).flatMap((agency) =>
    connectorsForAgency(db, agency.id),
  )
  return [...direct, ...subordinate]
}

export function managedAgencyIdsForPartner(db: Db, partnerUserId: string): string[] {
  const partner = findUser(db, partnerUserId)
  if (!isActiveUser(partner) || !isAgencyLikeRole(partner.role)) return []

  if (partner.role === '代理店') {
    return [partner.id]
  }

  return [partner.id, ...agenciesForSuperAgency(db, partner.id).map((agency) => agency.id)]
}

export function dealsVisibleToPartner(db: Db, partnerUserId: string): Deal[] {
  const agencyIds = new Set(managedAgencyIdsForPartner(db, partnerUserId))
  const connectorIds = new Set(
    Array.from(agencyIds).flatMap((agencyId) => connectorsForAgency(db, agencyId).map((connector) => connector.id)),
  )

  return db.deals.filter(
    (deal) =>
      (!!deal.assignedAgencyId && agencyIds.has(deal.assignedAgencyId))
      || (!!deal.connectorId && connectorIds.has(deal.connectorId)),
  )
}

export function getAssignedAgencyForDeal(db: Db, deal: Deal): User | undefined {
  if (deal.assignedAgencyId) {
    return findUser(db, deal.assignedAgencyId)
  }

  const connector = findUser(db, deal.connectorId)
  if (connector?.role !== 'コネクター') return undefined
  return findUser(db, connector.connector?.agencyId)
}

export function getSuperAgencyForDeal(db: Db, deal: Deal): User | undefined {
  const assignedAgency = getAssignedAgencyForDeal(db, deal)
  if (!assignedAgency) return undefined
  if (assignedAgency.role === '総代理店') return assignedAgency
  return findUser(db, assignedAgency.agency?.superAgencyId)
}

export function getDealParticipants(db: Db, deal: Deal): {
  connector?: User
  assignedAgency?: User
  superAgency?: User
} {
  const connector = findUser(db, deal.connectorId)
  const assignedAgency = getAssignedAgencyForDeal(db, deal)
  const superAgency = getSuperAgencyForDeal(db, deal)

  return {
    connector: connector?.role === 'コネクター' ? connector : undefined,
    assignedAgency,
    superAgency,
  }
}

export function sumAgencyRewards(
  db: Db,
  agencyUserId: string,
  status?: '未確定' | '確定' | '支払済み',
): number {
  return sumUserRewards(db, agencyUserId, status)
}

export function sumConnectorRewards(
  db: Db,
  connectorUserId: string,
  status?: '未確定' | '確定' | '支払済み',
): number {
  return sumUserRewards(db, connectorUserId, status)
}

export function sumUserRewards(db: Db, userId: string, status?: '未確定' | '確定' | '支払済み'): number {
  return db.transactions
    .flatMap((t) => t.allocations)
    .filter(
      (a) =>
        a.recipientType === 'ユーザー報酬'
        && a.userId === userId
        && (status ? a.status === status : true),
    )
    .reduce((sum, a) => sum + a.amountJPY, 0)
}

export function sumAgencyTeamSales(db: Db, agencyUserId: string): number {
  return db.transactions
    .filter((t) => t.agencyId === agencyUserId)
    .reduce((sum, t) => sum + t.saleAmountJPY, 0)
}

export function sumPartnerManagedSales(db: Db, partnerUserId: string): number {
  const agencyIds = new Set(managedAgencyIdsForPartner(db, partnerUserId))
  return db.transactions
    .filter((transaction) => agencyIds.has(transaction.agencyId))
    .reduce((sum, transaction) => sum + transaction.saleAmountJPY, 0)
}

export function sumConnectorSales(db: Db, connectorUserId: string): number {
  return db.transactions
    .filter((t) => t.connectorId === connectorUserId)
    .reduce((sum, t) => sum + t.saleAmountJPY, 0)
}
